import { Box, Breadcrumbs as MuiBreadcrumbs, Chip } from '@mui/material';
import { emphasize, styled } from '@mui/material/styles';
import type { BreadcrumbConfig, RouteHandle } from '@remix-run/router';
import { memo } from 'react';
import { Link as RouterLink, UIMatch } from 'react-router-dom';

import { useBreadcrumbsContext } from '@/contexts/breadcrumbs';
import { useTypedMatches } from '@/hooks/useTypedMatches.ts';

import CopyableBreadcrumbItem from './CopyableBreadcrumbItem';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.background.default
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
    '&:focus-visible': {
      outline: `4px solid ${theme.palette.primary.main}`,
      outlineOffset: '-1px',
    },
    cursor: 'pointer',
  };
}) as typeof Chip;

interface ResolvedBreadcrumb {
  label: string;
  path: string;
  icon?: BreadcrumbConfig['icon'];
  isCopyable?: boolean;
}

/**
 * Universal breadcrumbs component that automatically builds breadcrumb trail
 * from route handles using react-router's useMatches hook.
 *
 * Each route in the hierarchy defines its own breadcrumb segment:
 * ```tsx
 * <Route path="/customers" handle={{ crumb: { label: 'Customers', icon: FaceIcon } }}>
 *   <Route index element={<CustomerList />} />
 *   <Route
 *     path=":customerId"
 *     element={<CustomerDetail />}
 *     handle={{ crumb: { label: (params) => params.customerId || 'Detail' } }}
 *   />
 * </Route>
 * ```
 *
 * Breadcrumbs are automatically assembled from all matched routes with `crumb` handles.
 * Only renders if there are 2+ breadcrumb items (single items are not displayed).
 *
 * Pages can override the current (last) breadcrumb label using the `useBreadcrumbLabel` hook:
 * ```tsx
 * function CustomerDetail() {
 *   const { data: customer } = useGetCustomerById(customerId);
 *   useBreadcrumbLabel(customer?.name);
 * }
 * ```
 */
const Breadcrumbs = memo(() => {
  const matches = useTypedMatches();
  const { currentLabel } = useBreadcrumbsContext();

  const breadcrumbs: ResolvedBreadcrumb[] = matches
    .filter((match): match is UIMatch<unknown, Required<RouteHandle>> =>
      Boolean(match.handle?.crumb)
    )
    .map((match): ResolvedBreadcrumb => {
      const crumb = match.handle.crumb;

      const label =
        typeof crumb.label === 'function'
          ? crumb.label(match.params)
          : crumb.label;

      return {
        label,
        path: match.pathname,
        icon: crumb.icon,
        isCopyable: crumb.copyable ?? false,
      };
    });

  if (breadcrumbs.length <= 1) {
    return null;
  }

  const finalBreadcrumbs = breadcrumbs.map((crumb, index) => {
    const isLast = index === breadcrumbs.length - 1;
    if (isLast && currentLabel) {
      return { ...crumb, label: currentLabel };
    }
    return crumb;
  });

  return (
    <Box role="presentation" sx={{ mx: 2, mb: 1 }}>
      <MuiBreadcrumbs aria-label="breadcrumb">
        {finalBreadcrumbs.map((crumb, index) => {
          const isLast = index === finalBreadcrumbs.length - 1;
          const Icon = crumb.icon;

          if (isLast) {
            if (crumb.isCopyable) {
              return (
                <CopyableBreadcrumbItem
                  key={crumb.path}
                  label={crumb.label}
                  icon={Icon}
                />
              );
            }
            return (
              <StyledBreadcrumb
                key={crumb.path}
                size="medium"
                label={crumb.label}
                icon={Icon ? <Icon fontSize="small" /> : undefined}
                sx={{
                  cursor: 'default',
                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                }}
                aria-current="page"
              />
            );
          }

          return (
            <StyledBreadcrumb
              key={crumb.path}
              size="medium"
              sx={{ fontSize: (theme) => theme.typography.subtitle1.fontSize }}
              component={RouterLink}
              to={crumb.path}
              viewTransition
              label={crumb.label}
              icon={Icon ? <Icon fontSize="small" /> : undefined}
            />
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
});
Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
